import React, { useState, useEffect } from 'react';
import { PDFDocument, User } from '../types';
import * as api from '../services/api';
import { QRCodeCanvas } from 'qrcode.react';

interface DashboardProps {
  user: User;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [docs, setDocs] = useState<PDFDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [replacingId, setReplacingId] = useState<string | null>(null);

  useEffect(() => {
    loadDocs();
  }, [user]);

  const loadDocs = async () => {
    try {
      const documents = await api.listDocuments();
      setDocs(documents.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()));
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file.');
        return;
      }
      setSelectedFile(file);
      if (!uploadTitle) setUploadTitle(file.name.replace('.pdf', ''));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      let uploadUrl: string;
      let docId: string;

      if (replacingId) {
        // Update existing document with new file
        const result = await api.updateDocument(replacingId, {
          title: uploadTitle,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          newFile: true,
        });
        uploadUrl = result.uploadUrl!;
        docId = replacingId;
      } else {
        // Create new document
        const result = await api.createDocument(uploadTitle, selectedFile.name, selectedFile.size);
        uploadUrl = result.uploadUrl;
        docId = result.document.id;
      }

      // Upload file directly to R2 via presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: { 'Content-Type': 'application/pdf' },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage.');
      }

      // Confirm upload and trigger AI summarization
      await api.confirmUpload(docId);

      setUploadTitle('');
      setSelectedFile(null);
      setReplacingId(null);
      setIsUploading(false);

      const modal = document.getElementById('upload_modal') as HTMLDialogElement;
      if (modal) modal.close();

      loadDocs();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Failed to upload file.');
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this document? The QR code will stop working.')) {
      try {
        await api.deleteDocument(id);
        loadDocs();
      } catch (err) {
        console.error(err);
        alert('Failed to delete document.');
      }
    }
  };

  const downloadQR = (slug: string, title: string, format: 'png' | 'jpg') => {
    const canvas = document.getElementById(`qr-canvas-${slug}`) as HTMLCanvasElement;
    if (canvas) {
      const link = document.createElement('a');
      link.download = `${title.replace(/\s+/g, '_')}_QR.${format}`;
      link.href = canvas.toDataURL(`image/${format === 'jpg' ? 'jpeg' : 'png'}`, 1.0);
      link.click();
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const qrUrl = (slug: string) => `${window.location.origin}/v/${slug}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Management Console</h1>
          <p className="text-slate-500 mt-1">Deploy and track your persistent document QR codes.</p>
        </div>
        <button
          onClick={() => {
            setReplacingId(null);
            setUploadTitle('');
            setSelectedFile(null);
            (document.getElementById('upload_modal') as HTMLDialogElement)?.showModal();
          }}
          className="inline-flex items-center px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 active:translate-y-0 gap-3"
        >
          <i className="fas fa-plus"></i>
          Register New PDF
        </button>
      </div>

      {docs.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-200">
          <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-cloud-upload-alt text-4xl text-indigo-400"></i>
          </div>
          <h3 className="text-xl font-bold text-slate-900">No documents in orbit</h3>
          <p className="text-slate-500 mb-8 max-w-sm mx-auto">Upload a PDF to generate a persistent link that stays the same even if the content changes.</p>
          <button
            onClick={() => (document.getElementById('upload_modal') as HTMLDialogElement)?.showModal()}
            className="text-indigo-600 font-bold hover:underline"
          >
            Get started now &rarr;
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {docs.map((doc) => (
            <div key={doc.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden group hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-50/50 transition-all duration-300">
              <div className="p-8 flex flex-col sm:flex-row gap-8">
                <div className="flex-shrink-0 flex flex-col items-center gap-4">
                  <div className="p-3 bg-white border border-slate-100 rounded-2xl shadow-inner group-hover:scale-105 transition-transform duration-300">
                    <QRCodeCanvas
                      id={`qr-canvas-${doc.slug}`}
                      value={qrUrl(doc.slug)}
                      size={140}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <div className="flex w-full gap-2">
                    <button
                      onClick={() => downloadQR(doc.slug, doc.title, 'png')}
                      className="flex-1 text-[10px] py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors font-bold uppercase tracking-wider"
                    >
                      PNG
                    </button>
                    <button
                      onClick={() => downloadQR(doc.slug, doc.title, 'jpg')}
                      className="flex-1 text-[10px] py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors font-bold uppercase tracking-wider"
                    >
                      JPG
                    </button>
                  </div>
                </div>

                <div className="flex-grow flex flex-col min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">{doc.title}</h3>
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-2 font-medium">
                        <i className="fas fa-file-pdf text-red-400"></i> {doc.fileName} • {formatSize(doc.fileSize)}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-green-50 text-green-700 text-[10px] font-black uppercase rounded-full border border-green-100 tracking-widest flex-shrink-0">
                      Scannable
                    </span>
                  </div>

                  <div className="mt-5 text-sm text-slate-600 bg-slate-50/50 p-4 rounded-xl border-l-4 border-indigo-400 relative">
                    <i className="fas fa-quote-left absolute top-2 left-2 text-indigo-100 text-lg"></i>
                    <p className="line-clamp-3 italic pl-4">
                      {doc.summary || "Generating intelligent summary via Gemini AI..."}
                    </p>
                  </div>

                  <div className="mt-auto pt-6 flex flex-wrap gap-3">
                    <a
                      href={qrUrl(doc.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-black transition-all flex items-center gap-2 shadow-md active:scale-95"
                    >
                      <i className="fas fa-eye"></i> View Live
                    </a>
                    <button
                      onClick={() => {
                        setReplacingId(doc.id);
                        setUploadTitle(doc.title);
                        (document.getElementById('upload_modal') as HTMLDialogElement)?.showModal();
                      }}
                      className="px-4 py-2 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-lg hover:bg-indigo-100 transition-all flex items-center gap-2 active:scale-95"
                    >
                      <i className="fas fa-sync-alt"></i> Replace Content
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="px-4 py-2 bg-red-50 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 transition-all flex items-center gap-2 active:scale-95"
                    >
                      <i className="fas fa-trash-alt"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50/80 px-8 py-4 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                <div className="flex gap-4">
                  <span>Inbound: {new Date(doc.uploadDate).toLocaleDateString()}</span>
                  <span className="text-slate-300">|</span>
                  <span>Modified: {new Date(doc.lastUpdated).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1 text-indigo-400">
                  <i className="fas fa-link"></i>
                  <span>/{doc.slug}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <dialog id="upload_modal" className="modal bg-white rounded-3xl p-0 shadow-2xl w-full max-w-lg overflow-y-auto max-h-[90vh] backdrop:bg-slate-900/60 transition-all">
        <div className="p-8 border-b flex items-center justify-between bg-slate-50/50">
          <div>
            <h3 className="font-black text-2xl text-slate-900">
              {replacingId ? 'Update Content' : 'Deploy Document'}
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              {replacingId ? 'Replace the underlying PDF while keeping the same QR.' : 'Upload a PDF and generate a persistent QR code.'}
            </p>
          </div>
          <button
            onClick={() => (document.getElementById('upload_modal') as HTMLDialogElement)?.close()}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleUpload} className="p-8 space-y-8">
          <div className="space-y-2">
            <label className="text-sm font-black text-slate-700 uppercase tracking-widest flex items-center gap-2">
              <i className="fas fa-tag text-indigo-400"></i>
              Internal Alias
            </label>
            <input
              type="text"
              className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all font-medium"
              placeholder="e.g. Sales Deck 2024"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-black text-slate-700 uppercase tracking-widest flex items-center gap-2">
              <i className="fas fa-file-upload text-indigo-400"></i>
              Source Document (PDF)
            </label>
            <div className={`group relative flex justify-center px-8 py-10 border-4 border-dashed rounded-3xl transition-all duration-300 ${selectedFile ? 'border-indigo-400 bg-indigo-50/50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`}>
              <div className="space-y-2 text-center">
                {selectedFile ? (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mb-4 text-indigo-600 scale-110">
                      <i className="fas fa-file-pdf text-3xl"></i>
                    </div>
                    <p className="text-lg font-bold text-slate-900">{selectedFile.name}</p>
                    <p className="text-xs text-indigo-500 font-black uppercase tracking-widest mt-1">{formatSize(selectedFile.size)}</p>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="mt-6 text-sm text-red-500 font-bold flex items-center gap-2 hover:underline"
                    >
                      <i className="fas fa-times-circle"></i> Clear Selection
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 text-slate-400 group-hover:text-indigo-500 transition-colors">
                      <i className="fas fa-cloud-upload-alt text-3xl"></i>
                    </div>
                    <div className="flex text-lg text-slate-600 justify-center">
                      <label className="relative cursor-pointer bg-transparent font-bold text-indigo-600 hover:text-indigo-700">
                        <span>Select a file from device</span>
                        <input type="file" className="sr-only" accept=".pdf" onChange={handleFileChange} />
                      </label>
                    </div>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">PDF format only</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {replacingId && (
            <div className="bg-indigo-600 rounded-2xl p-5 text-white shadow-xl shadow-indigo-100 flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="fas fa-shield-alt text-lg"></i>
              </div>
              <div>
                <h4 className="font-bold text-sm">Persistence Mode Active</h4>
                <p className="text-xs text-indigo-100 mt-1 leading-relaxed">
                  Your QR code at <span className="font-mono bg-white/10 px-1 rounded">/v/{docs.find(d => d.id === replacingId)?.slug}</span> is reserved. The underlying content will be hot-swapped instantly.
                </p>
              </div>
            </div>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={!selectedFile || isUploading}
              className={`w-full py-5 rounded-2xl font-black text-lg text-white transition-all shadow-xl active:scale-95 ${isUploading || !selectedFile ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200'}`}
            >
              {isUploading ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Uploading & Synthesizing...
                </span>
              ) : replacingId ? 'Hot-Swap Document' : 'Initialize QR Code'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
};

export default Dashboard;
